import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, EmptyState } from '../../components/mobile/ManageKit';
import { DatePicker } from '../../components/ui/date-picker';
import { FileText, Loader2, CheckCircle2, Send, XCircle, MapPin, Calendar, Hash, User, ChevronLeft, Receipt, Upload, Pencil, Plus, Trash2, RotateCcw, Save } from 'lucide-react';

const STATUS_META = {
  draft: { label: 'Draft', cls: 'text-[var(--m-text-body)] bg-[var(--m-border-light)]/30' },
  pending_approval: { label: 'Pending', cls: 'text-amber-400 bg-amber-500/10' },
  submitted: { label: 'Submitted', cls: 'text-amber-400 bg-amber-500/10' },
  approved: { label: 'Approved', cls: 'text-green-400 bg-green-500/10' },
  exported: { label: 'Exported', cls: 'text-blue-400 bg-blue-500/10' },
};

const PENDING = ['pending_approval', 'submitted'];

const TABS = [
  { key: 'draft', label: 'Draft' },
  { key: 'pending_approval', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'all', label: 'All' },
];

const DATE_TABS = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'custom', label: 'Custom' },
];

const getDateRange = (key) => {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dayOfWeek = now.getDay(); // 0=Sun
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  switch (key) {
    case 'yesterday': {
      const d = new Date(now); d.setDate(d.getDate() - 1);
      return { start: fmt(d), end: fmt(d) };
    }
    case 'today':
      return { start: fmt(now), end: fmt(now) };
    case 'this_week': {
      const mon = new Date(now); mon.setDate(mon.getDate() + mondayOffset);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return { start: fmt(mon), end: fmt(sun) };
    }
    case 'last_week': {
      const mon = new Date(now); mon.setDate(mon.getDate() + mondayOffset - 7);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return { start: fmt(mon), end: fmt(sun) };
    }
    default:
      return null;
  }
};

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MobileManageDockets = () => {
  const { api, tenantSettings, can } = useMobileAuth();
  const canViewFinancial = can('invoicing.read') || can('accounting.read');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateTab, setDateTab] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [acting, setActing] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [viewMode, setViewMode] = useState('docket'); // 'docket' | 'invoice'
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [editWorkCompleted, setEditWorkCompleted] = useState('');

  const fmtDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('T')[0].split('-');
    if (!y || !m || !day) return d;
    return (tenantSettings?.date_format === 'MM/DD/YYYY') ? `${m}/${day}/${y}` : `${day}/${m}/${y}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const range = dateTab === 'custom'
        ? (customStart ? { start: customStart, end: customEnd || customStart } : null)
        : getDateRange(dateTab);
      const params = {};
      if (range) {
        params.start_date = range.start;
        params.end_date = range.end;
      }
      const res = await api.get('/api/dockets', { params });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load dockets');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [dateTab, customStart, customEnd]);

  const lineTotal = (it) => (Number(it.billed_qty || it.qty) || 0) * (Number(it.unit_price) || 0);
  const subtotal = (d) => (d?.items || []).reduce((s, it) => s + lineTotal(it), 0);

  const openDocket = (d) => {
    setActive(d);
    setConfirmReject(false);
    setViewMode('docket');
    setEditing(false);
    setOpen(true);
  };

  const doAction = async (path, successMsg) => {
    setActing(true);
    try {
      const res = await api.post(`/api/dockets/${active.id}/${path}`);
      toast.success(successMsg);
      setItems((prev) => prev.map((x) => (x.id === active.id ? { ...x, ...res.data } : x)));
      setActive((a) => ({ ...a, ...res.data }));
      setConfirmReject(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const connectedPlatforms = [
    tenantSettings?.xero_connected ? { key: 'xero', label: 'Xero', path: 'xero' } : null,
    tenantSettings?.myob_connected ? { key: 'myob', label: 'MYOB', path: 'myob' } : null,
    tenantSettings?.qbo_connected ? { key: 'quickbooks', label: 'QuickBooks', path: 'quickbooks' } : null,
  ].filter(Boolean);

  const doExport = async (platform) => {
    setActing(true);
    try {
      const res = await api.post(`/api/${platform.path}/push-invoice`, { docket_id: active.id });
      const num = res.data.invoice_number || res.data.xero_invoice_number || res.data.myob_invoice_number || res.data.qbo_invoice_number;
      toast.success(num ? `Invoice ${num} created in ${platform.label}` : `Invoice pushed to ${platform.label}`);
      setItems((prev) => prev.map((x) => (x.id === active.id ? { ...x, ...res.data, status: 'exported' } : x)));
      setActive((a) => ({ ...a, ...res.data, status: 'exported' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to export to ${platform.label}`);
    } finally {
      setActing(false);
    }
  };

  const startEditing = () => {
    setEditItems((active.items || []).map((it) => ({ ...it })));
    setEditNotes(active.notes || '');
    setEditWorkCompleted(active.work_completed || '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditItems([]);
    setEditNotes('');
    setEditWorkCompleted('');
  };

  const handleEditItem = (id, field, value) => {
    setEditItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value, ...(field === 'unit_price' ? { price_overridden: true } : {}), ...(field === 'description' ? { description_overridden: true } : {}) } : it)));
  };

  const handleAddLine = () => {
    setEditItems((prev) => [...prev, { id: `new-${Date.now()}`, item_type: 'equipment', type_name: '', display_name: '', description: '', qty: 0, uom: 'HOUR', unit_price: 0, invoice_added: true }]);
  };

  const handleRemoveLine = (id) => {
    setEditItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/dockets/${active.id}`, {
        items: editItems,
        notes: editNotes,
        work_completed: editWorkCompleted,
        version: active.version,
      });
      toast.success('Docket saved & invoice regenerated');
      const res = await api.get(`/api/dockets/${active.id}`);
      const updated = res.data;
      setActive(updated);
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setEditing(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('version')) {
        toast.error('This docket was modified elsewhere. Please close and reopen it.');
      } else {
        toast.error(detail || 'Failed to save docket');
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToDraft = async () => {
    setActing(true);
    try {
      const res = await api.post(`/api/dockets/${active.id}/reset`);
      toast.success('Docket reset to draft');
      const updated = { ...active, ...res.data, status: 'draft' };
      setActive(updated);
      setItems((prev) => prev.map((x) => (x.id === active.id ? updated : x)));
      startEditing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset docket');
    } finally {
      setActing(false);
    }
  };

  const STATUS_ORDER = { draft: 0, pending_approval: 1, submitted: 1, approved: 2, exported: 3 };

  const filtered = items
    .filter((d) => (statusFilter === 'all' ? true : statusFilter === 'pending_approval' ? PENDING.includes(d.status) : d.status === statusFilter))
    .filter((d) => `${d.docket_number || ''} ${d.customer_name || ''} ${d.site_name || ''}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  return (
    <MobileLayout>
      <ManageHeader title="Dockets" subtitle="Review & approve for invoicing" backTo="/mobile/manage" />
      <div className="bg-[var(--m-bg-card)] px-3 pt-2 pb-2 sticky top-[52px] z-10 space-y-2 -mt-px">
        {/* Date range tabs */}
        <div className="grid grid-cols-5 gap-1.5">
          {DATE_TABS.map((t) => (
            <button key={t.key} onClick={() => setDateTab(t.key)}
              className={`px-1 py-1.5 rounded-full text-[11px] font-medium transition-colors text-center ${dateTab === t.key ? 'bg-[#ffd108] text-[#1a1a1a]' : 'bg-[var(--m-bg)] text-[var(--m-text-secondary)]'}`}
              data-testid={`docket-date-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
        {/* Custom date range pickers */}
        {dateTab === 'custom' && (
          <div className="flex gap-2 items-center">
            <DatePicker value={customStart} onChange={setCustomStart}
              className="flex-1 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-xs" testId="custom-date-start" />
            <span className="text-[var(--m-text-muted)] text-xs">to</span>
            <DatePicker value={customEnd} onChange={setCustomEnd}
              className="flex-1 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-xs" testId="custom-date-end" />
          </div>
        )}
        {/* Status filter + search row */}
        <div className="flex gap-2 items-center">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-2 py-1.5 text-xs text-[var(--m-text)] shrink-0 appearance-none pr-6"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%239ca3af' viewBox='0 0 24 24'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
            data-testid="docket-status-filter">
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending</option>
            <option value="approved">Approved</option>
            <option value="exported">Exported</option>
          </select>
          <SearchBar value={q} onChange={setQ} placeholder="Search dockets..." />
        </div>
      </div>

      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No dockets" subtitle="Nothing matches this filter" />
        ) : (
          <div className="space-y-2" data-testid="dockets-list">
            {filtered.map((d) => {
              const meta = STATUS_META[d.status] || STATUS_META.draft;
              return (
                <button key={d.id} onClick={() => openDocket(d)}
                  className="w-full bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]"
                  data-testid={`docket-row-${d.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-[var(--m-text)] font-medium truncate">{d.customer_name || 'Unknown'}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="text-xs text-[var(--m-text-secondary)] truncate mt-0.5">
                    {d.docket_number ? `#${d.docket_number} · ` : ''}{d.site_name || 'No site'}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-[var(--m-text-muted)]">{fmtDate(d.job_date)} · {(d.items || []).length} lines</span>
                    <span className="text-sm text-[#ffd108] font-semibold">{money(subtotal(d))}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Full-screen docket detail */}
      {open && active && (() => {
        const meta = STATUS_META[active.status] || STATUS_META.draft;
        const hasInvoice = !!(active.xero_invoice_id || active.myob_invoice_id || active.qbo_invoice_id || active.status === 'exported');
        const invoiceNum = active.invoice_number || active.xero_invoice_number || active.myob_invoice_number || active.qbo_invoice_number || '';
        const accountingSystem = active.xero_invoice_id ? 'Xero' : active.myob_invoice_id ? 'MYOB' : active.qbo_invoice_id ? 'QuickBooks' : '';
        return (
          <div className="fixed inset-x-0 top-0 bottom-[calc(72px+env(safe-area-inset-bottom,0px))] z-[60] bg-[var(--m-bg)] flex flex-col" data-testid="docket-fullscreen">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--m-border)] shrink-0">
              <button onClick={() => setOpen(false)} className="p-1 -ml-1" data-testid="docket-back-btn">
                <ChevronLeft className="h-5 w-5 text-[var(--m-text-secondary)]" />
              </button>
              <FileText className="h-5 w-5 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium flex-1 truncate">
                {active.docket_number ? `Docket #${active.docket_number}` : 'Docket'}
              </span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
              {!editing && active.status === 'draft' && (
                <button onClick={startEditing} className="p-1.5 rounded-lg bg-[var(--m-bg-card)] text-[var(--m-text-secondary)] hover:text-[var(--m-text)]" data-testid="docket-edit-btn"><Pencil className="h-4 w-4" /></button>
              )}
              {!editing && active.status !== 'draft' && active.status !== 'exported' && (
                <button onClick={resetToDraft} disabled={acting} className="p-1.5 rounded-lg bg-[var(--m-bg-card)] text-[var(--m-text-secondary)] hover:text-[var(--m-text)]" data-testid="docket-reset-btn"><RotateCcw className="h-4 w-4" /></button>
              )}
            </div>

            {/* Docket / Invoice toggle — invoice tab requires financial access */}
            {canViewFinancial ? (
              <div className="flex gap-2 px-4 py-2.5 border-b border-[var(--m-border)] shrink-0" data-testid="view-toggle-bar">
                <button
                  onClick={() => setViewMode('docket')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    viewMode === 'docket'
                      ? 'bg-blue-600 text-[var(--m-text)]'
                      : 'bg-[var(--m-bg-card)] text-[var(--m-text-secondary)]'
                  }`}
                  data-testid="toggle-docket-btn"
                >
                  <FileText className="h-4 w-4" />
                  Docket
                </button>
                <button
                  onClick={() => setViewMode('invoice')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    viewMode === 'invoice'
                      ? 'bg-purple-600 text-[var(--m-text)]'
                      : 'bg-[var(--m-bg-card)] text-[var(--m-text-secondary)]'
                  }`}
                  data-testid="toggle-invoice-btn"
                >
                  <Receipt className="h-4 w-4" />
                  Invoice
                </button>
              </div>
            ) : (
              <div className="px-4 py-2.5 border-b border-[var(--m-border)] shrink-0">
                <div className="flex items-center gap-1.5 py-2 text-sm font-semibold text-[var(--m-text)]">
                  <FileText className="h-4 w-4" />
                  Docket Details
                </div>
              </div>
            )}

            {/* Scrollable content */}
            {viewMode === 'docket' ? (
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="docket-detail">
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-[var(--m-text-body)]"><User className="h-4 w-4 text-[var(--m-text-muted)]" /> {active.customer_name || 'Unknown customer'}</div>
                <div className="flex items-center gap-2 text-[var(--m-text-body)]"><MapPin className="h-4 w-4 text-[var(--m-text-muted)]" /> {active.site_name || 'No site'}</div>
                <div className="flex items-center gap-2 text-[var(--m-text-body)]"><Calendar className="h-4 w-4 text-[var(--m-text-muted)]" /> {fmtDate(active.job_date)}</div>
                {active.po_number && <div className="flex items-center gap-2 text-[var(--m-text-body)]"><Hash className="h-4 w-4 text-[var(--m-text-muted)]" /> PO {active.po_number}</div>}
              </div>

              <div className="border-t border-[var(--m-border)] pt-3">
                <p className="text-xs font-semibold text-[var(--m-text-secondary)] uppercase tracking-wide mb-2">Line items</p>
                {((editing ? editItems : active.items) || []).length === 0 ? (
                  <p className="text-sm text-[var(--m-text-muted)]">No line items</p>
                ) : (
                  <div className="space-y-2">
                    {(editing ? editItems : active.items || []).map((it, i) => (
                      <div key={it.id || i} className="flex items-start gap-3 bg-[var(--m-bg-card)] rounded-lg px-3 py-2" data-testid={`docket-line-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm text-[var(--m-text)] truncate">{it.display_name || it.type_name || 'Item'}</span>
                            {it.included_in_asset && (
                              <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400" title={it.included_note || 'Included in asset rate'} data-testid={`included-badge-mobile-${i}`}>Included</span>
                            )}
                          </div>
                          {editing ? (
                            <input type="number" inputMode="decimal" value={it.qty} onChange={(e) => handleEditItem(it.id, 'qty', e.target.value)}
                              className="mt-1 w-20 bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded px-2 py-1 text-xs text-[var(--m-text)]" data-testid={`docket-line-qty-${i}`} />
                          ) : (
                            <div className="text-xs text-[var(--m-text-secondary)]">{(Number(it.billed_qty || it.qty) || 0)} {it.uom?.toLowerCase()}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-[var(--m-border)] pt-3 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-[var(--m-text-secondary)] uppercase mb-1">Work completed</p>
                  {editing ? (
                    <textarea value={editWorkCompleted} onChange={(e) => setEditWorkCompleted(e.target.value)} rows={3}
                      className="w-full bg-[var(--m-bg-card)] border border-[var(--m-border-light)] rounded-lg px-3 py-2 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)] resize-none"
                      placeholder="Describe work completed..." data-testid="edit-work-completed" />
                  ) : (
                    <p className="text-sm text-[var(--m-text-body)] whitespace-pre-wrap">{active.work_completed || <span className="text-gray-600">None</span>}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--m-text-secondary)] uppercase mb-1">Notes</p>
                  {editing ? (
                    <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={2}
                      className="w-full bg-[var(--m-bg-card)] border border-[var(--m-border-light)] rounded-lg px-3 py-2 text-sm text-[var(--m-text)] placeholder-[var(--m-text-muted)] resize-none"
                      placeholder="Additional notes..." data-testid="edit-notes" />
                  ) : (
                    <p className="text-sm text-[var(--m-text-body)] whitespace-pre-wrap">{active.notes || <span className="text-gray-600">None</span>}</p>
                  )}
                </div>
              </div>
            </div>
            ) : (
            /* Invoice view */
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="invoice-detail">
              {/* Invoice header info */}
              <div className="space-y-1.5 text-sm">
                {invoiceNum && (
                  <div className="flex items-center gap-2 text-[var(--m-text)]">
                    <Receipt className="h-4 w-4 text-purple-400" />
                    <span className="font-medium">Invoice #{invoiceNum}</span>
                  </div>
                )}
                {accountingSystem && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">{accountingSystem}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-[var(--m-text-body)]"><User className="h-4 w-4 text-[var(--m-text-muted)]" /> {active.customer_name || 'Unknown customer'}</div>
                <div className="flex items-center gap-2 text-[var(--m-text-body)]"><MapPin className="h-4 w-4 text-[var(--m-text-muted)]" /> {active.site_name || 'No site'}</div>
                <div className="flex items-center gap-2 text-[var(--m-text-body)]"><Calendar className="h-4 w-4 text-[var(--m-text-muted)]" /> {fmtDate(active.job_date)}</div>
                {active.po_number && <div className="flex items-center gap-2 text-[var(--m-text-body)]"><Hash className="h-4 w-4 text-[var(--m-text-muted)]" /> PO {active.po_number}</div>}
                {active.invoice_reference && <div className="flex items-center gap-2 text-[var(--m-text-body)]"><Hash className="h-4 w-4 text-[var(--m-text-muted)]" /> Ref: {active.invoice_reference}</div>}
              </div>

              {/* Invoice line items */}
              <div className="border-t border-[var(--m-border)] pt-3">
                <p className="text-xs font-semibold text-[var(--m-text-secondary)] uppercase tracking-wide mb-2">Invoice lines</p>
                {(() => {
                  const displayItems = (editing ? editItems : active.items || []).filter(it => !it.on_customer_account && !it.included_in_asset);
                  return displayItems.length === 0 ? (
                    <p className="text-sm text-[var(--m-text-muted)]">No line items</p>
                  ) : (
                    <div className="space-y-2">
                      {displayItems.map((it, i) => (
                        <div key={it.id || i} className="bg-[var(--m-bg-card)] rounded-lg px-3 py-2" data-testid={`invoice-line-${i}`}>
                          {editing ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <input value={it.description || it.display_name || it.type_name || ''} onChange={(e) => handleEditItem(it.id, 'description', e.target.value)}
                                  className="flex-1 bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded px-2 py-1 text-sm text-[var(--m-text)]" placeholder="Description" data-testid={`invoice-line-desc-${i}`} />
                                <button onClick={() => handleRemoveLine(it.id)} className="p-1 text-red-400 hover:text-red-300 shrink-0" data-testid={`invoice-line-remove-${i}`}>
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <label className="text-[10px] text-[var(--m-text-muted)] uppercase">Qty</label>
                                  <input type="number" inputMode="decimal" value={it.qty} onChange={(e) => handleEditItem(it.id, 'qty', e.target.value)}
                                    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded px-2 py-1 text-sm text-[var(--m-text)]" data-testid={`invoice-line-qty-${i}`} />
                                </div>
                                <div className="flex-1">
                                  <label className="text-[10px] text-[var(--m-text-muted)] uppercase">Unit Price</label>
                                  <input type="number" inputMode="decimal" step="0.01" value={it.unit_price} onChange={(e) => handleEditItem(it.id, 'unit_price', e.target.value)}
                                    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded px-2 py-1 text-sm text-[var(--m-text)]" data-testid={`invoice-line-price-${i}`} />
                                </div>
                                <div className="text-right pt-4">
                                  <span className="text-sm text-[var(--m-text)] font-medium">{money(lineTotal(it))}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm text-[var(--m-text)] truncate">{it.description || it.display_name || it.type_name || 'Item'}</div>
                                <div className="text-xs text-[var(--m-text-secondary)]">
                                  {(Number(it.billed_qty || it.qty) || 0)} {it.uom?.toLowerCase()} × {money(it.unit_price)}
                                  {it.account_code && <span className="ml-1.5 text-gray-600">({it.account_code})</span>}
                                </div>
                              </div>
                              <div className="text-sm text-[var(--m-text)] font-medium shrink-0">{money(lineTotal(it))}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {editing && (
                  <button onClick={handleAddLine} className="mt-2 flex items-center gap-1.5 text-[#ffd108] text-sm font-medium w-full justify-center py-2 rounded-lg border border-dashed border-[var(--m-border-light)] hover:border-[var(--m-border-light)]" data-testid="invoice-add-line">
                    <Plus className="h-4 w-4" /> Add line item
                  </button>
                )}
                {/* Totals */}
                {(() => {
                  const invoiceItems = (editing ? editItems : active.items || []).filter(it => !it.on_customer_account);
                  const sub = invoiceItems.reduce((s, it) => s + lineTotal(it), 0);
                  const taxRate = (tenantSettings?.tax_name && tenantSettings.tax_name !== 'None') ? (tenantSettings?.tax_rate || 0) : 0;
                  const taxAmt = sub * (taxRate / 100);
                  const total = sub + taxAmt;
                  const taxLabel = tenantSettings?.tax_name || 'GST';
                  return (
                    <div className="mt-3 pt-3 border-t border-[var(--m-border)] space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[var(--m-text-secondary)]">Subtotal (ex {taxLabel})</span>
                        <span className="text-sm text-[var(--m-text)] font-medium">{money(sub)}</span>
                      </div>
                      {taxRate > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[var(--m-text-secondary)]">{taxLabel} ({taxRate}%)</span>
                          <span className="text-sm text-[var(--m-text)] font-medium">{money(taxAmt)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-1.5">
                        <span className="text-sm font-semibold text-[var(--m-text-body)]">Total (inc {taxLabel})</span>
                        <span className="text-lg font-bold text-purple-400">{money(total)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Sent status */}
              {active.invoice_sent_at && (
                <div className="border-t border-[var(--m-border)] pt-3">
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Send className="h-4 w-4" />
                    <span>Sent to {active.invoice_sent_to || 'customer'} on {fmtDate(active.invoice_sent_at)}</span>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Fixed bottom actions */}
            <div className="shrink-0 px-4 py-3 border-t border-[var(--m-border)] space-y-2 bg-[var(--m-bg)]">
              {editing && (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={cancelEditing} disabled={saving} data-testid="docket-cancel-edit">Cancel</Button>
                  <Button className="flex-1 btn-volt" onClick={handleSave} disabled={saving} data-testid="docket-save-edit">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}
                  </Button>
                </div>
              )}
              {!editing && active.status === 'draft' && (
                <Button className="btn-volt w-full" onClick={() => doAction('submit-for-approval', 'Submitted for approval')} disabled={acting} data-testid="docket-submit">
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Submit for approval</>}
                </Button>
              )}
              {!editing && PENDING.includes(active.status) && (
                <>
                  <Button className="btn-volt w-full" onClick={() => doAction('approve', 'Docket approved')} disabled={acting} data-testid="docket-approve">
                    {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Approve</>}
                  </Button>
                  {confirmReject ? (
                    <Button variant="destructive" className="w-full" onClick={() => doAction('reject', 'Docket rejected to draft')} disabled={acting} data-testid="docket-reject-confirm">Tap again to send back to draft</Button>
                  ) : (
                    <Button variant="outline" className="w-full border-red-800 text-red-400" onClick={() => setConfirmReject(true)} data-testid="docket-reject"><XCircle className="h-4 w-4 mr-2" /> Reject</Button>
                  )}
                </>
              )}
              {!editing && active.status === 'approved' && connectedPlatforms.length > 0 && canViewFinancial && (
                <div className="space-y-2">
                  <p className="text-xs text-[var(--m-text-secondary)] text-center mb-1">Export to accounting</p>
                  {connectedPlatforms.map((p) => (
                    <Button key={p.key} className="w-full bg-purple-600 hover:bg-purple-700 text-[var(--m-text)]" onClick={() => doExport(p)} disabled={acting} data-testid={`docket-export-${p.key}`}>
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Push to {p.label}</>}
                    </Button>
                  ))}
                </div>
              )}
              {!editing && active.status === 'approved' && connectedPlatforms.length === 0 && canViewFinancial && (
                <div className="flex items-center justify-center gap-2 text-green-400 text-sm py-2" data-testid="docket-approved-state">
                  <CheckCircle2 className="h-4 w-4" /> Approved{active.approved_at ? ` · ${fmtDate(active.approved_at)}` : ''}
                  <span className="text-[var(--m-text-muted)] text-xs ml-2">Connect accounting to export</span>
                </div>
              )}
              {!editing && active.status === 'exported' && (
                <div className="flex items-center justify-center gap-2 text-blue-400 text-sm py-2" data-testid="docket-exported-state">
                  <CheckCircle2 className="h-4 w-4" /> Exported
                  {(active.xero_invoice_number || active.myob_invoice_number || active.qbo_invoice_number) && 
                    <span className="text-[var(--m-text-secondary)]">· Invoice #{active.xero_invoice_number || active.myob_invoice_number || active.qbo_invoice_number}</span>
                  }
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </MobileLayout>
  );
};

export default MobileManageDockets;
